import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@workspace/ui/components/ui/dialog";
import { bookingsApi, type PriceQuote } from "@/lib/bookings-api";
import { api, type AvailabilityData, type DeliveryAirport } from "@/lib/api";
import { CheckoutDatesEditor } from "@/components/checkout/CheckoutDatesEditor";
import { CheckoutLocationEditor } from "@/components/checkout/CheckoutLocationEditor";
import {
  CheckoutRentalTypeEditor,
  type RentalType,
  type RentalTypeOption,
} from "@/components/checkout/CheckoutRentalTypeEditor";
import { computeMinRentalFee } from "@/components/booking/RequestChangeModal";
import { getCurrencySymbol, formatPrice } from "@workspace/api-client-react";
import { currencySymbolPadding } from "@/lib/utils";
import { hhmmTo12h, time12ToHhmm, getDefaultTimes } from "@/lib/trip-datetime";

/**
 * Task #4316 — host "Build a trip" quote builder.
 *
 * The host opens this from a conversation (no live trip) and assembles a
 * custom quote: dates/times, pick-up vs delivery, rental type, and an
 * overridden rental fee (+ optional delivery fee). We re-use the SAME
 * editors and the SAME canonical `getQuote` (with `includeFloors`) that the
 * standard checkout and the Request-a-change modal use — there is no second
 * pricing engine here. On send we POST the quote; the server snapshots the
 * priced terms, posts a `trip_quote` system card into the thread, and the
 * renter pays-as-is on the standard checkout (host send == consent).
 */

interface BuildTripCar {
  id: number;
  make: string;
  model: string;
  year: number;
  slug: string;
  city?: string;
  state?: string;
  latitude?: number | null;
  longitude?: number | null;
  country_code?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  conversationId: number;
  car: BuildTripCar;
  token: string | null;
  /** Currency for the listing — falls back to USD. */
  currency?: string | null;
  /** Called after a quote is successfully posted to the thread. */
  onSent?: () => void;
}

// Derive the half-hour-snapped `hours` span from a "HH:MM"–"HH:MM" pair.
// Mirrors CheckoutDatesEditor's chauffeur branch (Math.max(ceil(span*2)/2, 1))
// and the renter widget's quantizeHours so the chauffeur quote prices the
// same duration no matter which surface seeded the times. For the seeded
// 8am–10am default this yields 2.0.
function deriveHalfHourSpan(pickupHhmm: string, dropoffHhmm: string): number {
  const toHours = (hhmm: string) => {
    const [h, m] = hhmm.split(":").map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
    return h + m / 60;
  };
  const span = toHours(dropoffHhmm) - toHours(pickupHhmm);
  return Math.max(Math.ceil(span * 2) / 2, 1);
}

export function BuildTripModal({
  open,
  onClose,
  conversationId,
  car,
  token,
  currency: currencyProp,
  onSent,
}: Props) {
  const { t } = useTranslation();

  // `conv.car` does not carry a currency, so seed from the prop and refine
  // from the car-detail fetch below once it lands.
  const [currency, setCurrency] = useState(currencyProp || "USD");
  const sym = getCurrencySymbol(currency);

  // ---- trip terms -------------------------------------------------------
  const [rentalType, setRentalType] = useState<RentalType>("driving");
  // Dates/times start EMPTY — the host must enter them; we never pre-build a
  // trip. An empty dateFrom/dateTo is the "not entered yet" signal that gates
  // both the summary row and the live quote below.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // 24h "HH:MM" internally; converted to "h:mm am/pm" for the editor.
  // Times are PRE-FILLED with the rental-type defaults (mirroring the renter
  // BookingWidget via the shared getDefaultTimes), so the host doesn't have
  // to type them on every quote. Dates still start empty — they remain the
  // "not entered yet" gate for the summary and live quote.
  const driveDefaults = getDefaultTimes("driving");
  const [pickupTime, setPickupTime] = useState(() => time12ToHhmm(driveDefaults.pickup));
  const [dropoffTime, setDropoffTime] = useState(() => time12ToHhmm(driveDefaults.dropoff));
  const [hours, setHours] = useState<number | undefined>(undefined);

  const [isDelivery, setIsDelivery] = useState(false);
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [airportCode, setAirportCode] = useState<string | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState<string | null>(null);
  const [pickupLat, setPickupLat] = useState<number | null>(null);
  const [pickupLng, setPickupLng] = useState<number | null>(null);

  // ---- overrides --------------------------------------------------------
  const [rentalFee, setRentalFee] = useState("");
  const [rentalFeeTouched, setRentalFeeTouched] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState("");
  const [deliveryFeeTouched, setDeliveryFeeTouched] = useState(false);
  const [message, setMessage] = useState("");

  // ---- car config (delivery + rental availability) ----------------------
  const [carDeliveryEnabled, setCarDeliveryEnabled] = useState(false);
  const [selfDriveDeliveryEnabled, setSelfDriveDeliveryEnabled] = useState(false);
  const [carDeliveryRadius, setCarDeliveryRadius] = useState<number | null>(null);
  const [carDeliveryAirports, setCarDeliveryAirports] = useState<DeliveryAirport[]>([]);
  const [rentalOptions, setRentalOptions] = useState<RentalTypeOption[]>([]);
  const [availability, setAvailability] = useState<AvailabilityData | null>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // ---- live quote -------------------------------------------------------
  const [quote, setQuote] = useState<PriceQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const quoteVersionRef = useRef(0);

  // ---- send -------------------------------------------------------------
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Reset transient state each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setRentalType("driving");
    setDateFrom("");
    setDateTo("");
    // Re-seed times to the (driving) defaults each open; dates stay empty.
    const d = getDefaultTimes("driving");
    setPickupTime(time12ToHhmm(d.pickup));
    setDropoffTime(time12ToHhmm(d.dropoff));
    setHours(undefined);
    setIsDelivery(false);
    setDeliveryDistance(null);
    setAirportCode(null);
    setDeliveryAddress(null);
    setPickupLat(null);
    setPickupLng(null);
    setRentalFee("");
    setRentalFeeTouched(false);
    setDeliveryFee("");
    setDeliveryFeeTouched(false);
    setMessage("");
    setQuote(null);
    setQuoteError(null);
    setSendError(null);
  }, [open]);

  // Re-seed the pick-up / drop-off times whenever the rental type changes —
  // both the auto-switch to the car's first available type after the
  // car-detail fetch and any host-driven switch via the rental-type editor.
  // This mirrors the renter BookingWidget's tab-switch behaviour so the
  // host always starts from a valid 8am window for the new type (8am–10pm
  // for self-drive, 8am–10am for chauffeur/event — the latter still yields
  // a positive hours span). Dates are deliberately left untouched, and the
  // skip-first-mount ref means a host's manual time edits are only ever
  // overwritten by an intentional rental-type change, never by a re-render.
  const rentalTypeSeedRef = useRef(true);
  useEffect(() => {
    if (rentalTypeSeedRef.current) {
      rentalTypeSeedRef.current = false;
      return;
    }
    const d = getDefaultTimes(rentalType);
    const pickup = time12ToHhmm(d.pickup);
    const dropoff = time12ToHhmm(d.dropoff);
    setPickupTime(pickup);
    setDropoffTime(dropoff);
    // Chauffeur is billed hourly-per-day, so the live quote needs a derived
    // `hours` span (mirrors CheckoutDatesEditor's needsDerivedHours). Without
    // this, switching to chauffeured while dates are already picked would
    // leave hours undefined and the server would fall back to its 8-hour
    // default instead of pricing the intended 2-hour 8am–10am window. Driving
    // and event don't use a derived hours value (event bills by true 24h
    // periods from pickup/dropoff times), so leave it undefined for them.
    setHours(rentalType === "chauffeured" ? deriveHalfHourSpan(pickup, dropoff) : undefined);
  }, [rentalType]);

  useEffect(() => {
    if (rentalType === "driving" && !selfDriveDeliveryEnabled) {
      setIsDelivery(false);
      setDeliveryDistance(null);
      setAirportCode(null);
      setDeliveryAddress(null);
      setPickupLat(null);
      setPickupLng(null);
    }
  }, [rentalType, selfDriveDeliveryEnabled]);

  // Lazy car detail fetch — delivery airports + radius + rental options.
  useEffect(() => {
    if (!open || !car?.id || !car?.slug) return;
    api
      .getCarDetail(car.id, car.slug)
      .then((detail) => {
        setCarDeliveryAirports(detail.deliveryAirports || []);
        interface CarFlags {
          delivery_enabled?: boolean | null;
          self_drive_delivery_enabled?: boolean | null;
          delivery_max_distance?: number | null;
          deliveryMaxDistance?: number | null;
          rental_driving_active?: boolean | null;
          rental_chauffeured_active?: boolean | null;
          rental_event_active?: boolean | null;
          price_driving?: number | null;
          currency_code?: string | null;
          local_currency_code?: string | null;
        }
        const c = detail.car as unknown as CarFlags;
        const resolvedCurrency = c.currency_code || c.local_currency_code;
        if (resolvedCurrency) setCurrency(resolvedCurrency);
        setCarDeliveryEnabled(!!c.delivery_enabled);
        setSelfDriveDeliveryEnabled(c.self_drive_delivery_enabled === true);
        const radius = c.delivery_max_distance ?? c.deliveryMaxDistance;
        setCarDeliveryRadius(typeof radius === "number" ? radius : null);
        // Flag-authoritative: self-drive is offered only when the explicit
        // `rental_driving_active` flag is on. No `price_driving > 0` fallback —
        // the live V1 legacy writer keeps `price_driving` populated from the
        // legacy self-drive rate even on chauffeur-only imported listings, so a
        // price fallback would re-offer self-drive the host never enabled
        // (task #6016). Matches CarDetailPage / CheckoutPage.
        const drivingActive = !!c.rental_driving_active;
        const options: RentalTypeOption[] = [
          { key: "driving", label: t("rental_type.driving", "Self-drive"), available: drivingActive },
          { key: "chauffeured", label: t("rental_type.chauffeured", "Chauffeur"), available: !!c.rental_chauffeured_active },
          { key: "event", label: t("rental_type.event", "Event hire"), available: !!c.rental_event_active },
        ];
        setRentalOptions(options);
        const firstAvailable = options.find((o) => o.available)?.key;
        if (firstAvailable) setRentalType(firstAvailable);
      })
      .catch((err) => {
        console.warn("[BuildTripModal] car detail fetch failed (non-fatal):", err);
      });
  }, [open, car?.id, car?.slug, t]);

  // Availability for the calendar.
  useEffect(() => {
    if (!open || !car?.id || !car?.slug) return;
    setLoadingAvailability(true);
    api
      .getCarAvailability(car.id, car.slug, { token })
      .then((data) => setAvailability(data))
      .catch(() => setAvailability(null))
      .finally(() => setLoadingAvailability(false));
  }, [open, car?.id, car?.slug, token]);

  // Chauffeured + event are driven / delivered to the renter — there is no
  // self-pickup, so they are delivery-only (mirror the listing wizard + RCM).
  const deliveryOnly = rentalType === "event" || rentalType === "chauffeured";
  useEffect(() => {
    if (deliveryOnly && !isDelivery) setIsDelivery(true);
  }, [deliveryOnly, isDelivery]);

  const pickup12 = useMemo(() => hhmmTo12h(pickupTime, "10:00 am"), [pickupTime]);
  const dropoff12 = useMemo(() => hhmmTo12h(dropoffTime, "10:00 am"), [dropoffTime]);

  // Chauffeured is billed hourly-per-day, so the billable span MUST be derived
  // from the actual pick-up/drop-off times — never read off the `hours` state,
  // which can go stale or undefined across a rental-type switch / dates-editor
  // interplay between successive opens. A stale/undefined `hours` made the
  // server fall back to its 8-hour default and priced an 8-hour day instead of
  // the intended window (the "way off" Build-a-trip chauffeured price). Driving
  // ignores hours and event bills by true 24h periods, so both stay undefined.
  // This mirrors the renter widget's quantizeHours / CheckoutDatesEditor span,
  // guaranteeing the host quote prices the identical duration the renter pays.
  const effectiveHours = useMemo(
    () =>
      rentalType === "chauffeured"
        ? deriveHalfHourSpan(pickupTime, dropoffTime)
        : undefined,
    [rentalType, pickupTime, dropoffTime],
  );

  // The host must fill in the trip before we price it. Dates are always
  // required; delivery-only types also need a delivery location.
  const datesEntered = !!dateFrom && !!dateTo;
  const locationRequired = deliveryOnly;
  const deliveryAllowedForType =
    rentalType === "driving" ? selfDriveDeliveryEnabled : carDeliveryEnabled;
  const effectiveIsDelivery = deliveryAllowedForType && isDelivery;
  const locationEntered =
    !locationRequired ||
    (effectiveIsDelivery && (!!airportCode || (deliveryDistance != null && deliveryDistance > 0)));
  const detailsComplete = datesEntered && locationEntered;

  // Mirror RequestChangeModal: auto-fill the fee inputs with the freshly
  // calculated amounts so the host sees the price and can send the quote
  // as-is, while still being free to type an override. A field the host has
  // touched is never clobbered by a recalc.
  useEffect(() => {
    if (!quote) return;
    if (!rentalFeeTouched && Number.isFinite(quote.rentalFee)) {
      setRentalFee(Number(quote.rentalFee).toFixed(2));
    }
    if (!deliveryFeeTouched) {
      setDeliveryFee(
        effectiveIsDelivery && Number.isFinite(quote.deliveryFee)
          ? Number(quote.deliveryFee).toFixed(2)
          : "",
      );
    }
  }, [quote, rentalFeeTouched, deliveryFeeTouched, effectiveIsDelivery]);

  // Debounced live quote against the canonical pricing path with floors.
  useEffect(() => {
    const version = ++quoteVersionRef.current;
    if (!open || !car?.id) return;
    // Don't price (or show anything) until the host has entered the trip —
    // dates, times, and a delivery location when the rental type requires it.
    if (!detailsComplete) {
      setQuote(null);
      setQuoteError(null);
      setQuoteLoading(false);
      return;
    }
    const carId = car.id;
    setQuoteLoading(true);
    setQuoteError(null);
    const handle = setTimeout(() => {
      const distanceParam = effectiveIsDelivery && !airportCode ? deliveryDistance ?? 0 : 0;
      const airportParam = effectiveIsDelivery ? airportCode ?? "" : "";
      const rentalOverrideNum = Number(rentalFee);
      const rentalOverrideParam =
        rentalFeeTouched && rentalFee !== "" && Number.isFinite(rentalOverrideNum)
          ? rentalOverrideNum
          : undefined;
      const deliveryOverrideNum = Number(deliveryFee);
      const deliveryOverrideParam =
        effectiveIsDelivery && deliveryFeeTouched && deliveryFee !== "" && Number.isFinite(deliveryOverrideNum)
          ? deliveryOverrideNum
          : undefined;
      bookingsApi
        .getQuote(
          carId,
          dateFrom,
          dateTo,
          rentalType,
          undefined,
          undefined,
          undefined,
          effectiveHours,
          distanceParam || undefined,
          airportParam || undefined,
          pickupTime || undefined,
          dropoffTime || undefined,
          token ?? undefined,
          undefined,
          true,
          null,
          rentalOverrideParam,
          deliveryOverrideParam,
          effectiveIsDelivery ? deliveryAddress : null,
          effectiveIsDelivery ? pickupLat : null,
          effectiveIsDelivery ? pickupLng : null,
        )
        .then((q) => {
          if (quoteVersionRef.current !== version) return;
          setQuote(q);
          setQuoteError(null);
        })
        .catch((err) => {
          if (quoteVersionRef.current !== version) return;
          const msg = err instanceof Error ? err.message : String(err);
          setQuoteError(msg);
        })
        .finally(() => {
          if (quoteVersionRef.current !== version) return;
          setQuoteLoading(false);
        });
    }, 350);
    return () => clearTimeout(handle);
  }, [
    open,
    car?.id,
    dateFrom,
    dateTo,
    pickupTime,
    dropoffTime,
    rentalType,
    hours,
    effectiveIsDelivery,
    deliveryDistance,
    airportCode,
    deliveryAddress,
    pickupLat,
    pickupLng,
    token,
    rentalFee,
    rentalFeeTouched,
    deliveryFee,
    deliveryFeeTouched,
    detailsComplete,
  ]);

  const rentalFloor = useMemo(() => computeMinRentalFee(quote), [quote]);
  const rentalFeeBelowFloor = useMemo(() => {
    if (!rentalFeeTouched || rentalFee === "") return false;
    const n = Number(rentalFee);
    if (!Number.isFinite(n)) return false;
    return rentalFloor.available && rentalFloor.amount > 0 && n < rentalFloor.amount;
  }, [rentalFeeTouched, rentalFee, rentalFloor]);

  const rentalTypeChangeable = useMemo(
    () => rentalOptions.filter((o) => o.available).length > 1,
    [rentalOptions],
  );

  const rentalFeeProvided = rentalFee !== "" && Number.isFinite(Number(rentalFee));
  const sendDisabled =
    sending ||
    !detailsComplete ||
    quoteLoading ||
    !!quoteError ||
    !rentalFeeProvided ||
    rentalFeeBelowFloor ||
    !quote;

  const handleSend = async () => {
    if (!token || sendDisabled) return;
    setSending(true);
    setSendError(null);
    try {
      await bookingsApi.createTripQuote(token, {
        conversationId,
        carId: car.id,
        rentalType,
        dateFrom,
        dateTo,
        pickupTime: pickupTime || undefined,
        dropoffTime: dropoffTime || undefined,
        hours: effectiveHours,
        isDelivery: effectiveIsDelivery,
        deliveryDistance: effectiveIsDelivery && deliveryDistance != null ? deliveryDistance : undefined,
        airportCode: effectiveIsDelivery && airportCode ? airportCode : undefined,
        deliveryAddress: effectiveIsDelivery && deliveryAddress ? deliveryAddress : undefined,
        pickupLatitude: effectiveIsDelivery ? pickupLat ?? undefined : undefined,
        pickupLongitude: effectiveIsDelivery ? pickupLng ?? undefined : undefined,
        rentalFeeOverride: Number(rentalFee),
        deliveryFeeOverride: effectiveIsDelivery && deliveryFee !== "" ? Number(deliveryFee) : undefined,
        cancellationPolicy: undefined,
        message: message.trim() || undefined,
      });
      onSent?.();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSendError(msg);
    } finally {
      setSending(false);
    }
  };

  const locationSummary = effectiveIsDelivery
    ? deliveryAddress
      ? deliveryAddress
      : airportCode
        ? t("checkout.airport_delivery", "Airport delivery") + ` (${airportCode})`
        : locationRequired && !locationEntered
          ? t("conversation.add_location", "Add delivery location")
          : t("checkout.delivery", "Delivery")
    : t("checkout.self_pickup", "Pick up at location");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("conversation.build_trip", "Build a trip")}</DialogTitle>
          <DialogDescription>
            {t("conversation.build_trip_desc", "Send a custom quote. The renter pays as-is to confirm instantly.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Rental type */}
          {rentalOptions.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5a6b5a]">
                {t("conversation.rental_type", "Rental type")}
              </p>
              <div className="mt-1.5">
                <CheckoutRentalTypeEditor
                  current={rentalType}
                  options={rentalOptions}
                  onSelect={(next) => setRentalType(next)}
                  trigger={
                    rentalTypeChangeable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-brand/30 bg-brand/5 px-3 py-1.5 text-sm font-medium text-brand transition-colors hover:bg-brand/10"
                      >
                        {rentalOptions.find((o) => o.key === rentalType)?.label ?? rentalType}
                        <ChevronDown className="h-4 w-4" aria-hidden="true" />
                      </button>
                    ) : (
                      <span className="text-sm font-medium text-brand">
                        {rentalOptions.find((o) => o.key === rentalType)?.label ?? rentalType}
                      </span>
                    )
                  }
                />
              </div>
              {rentalTypeChangeable && (
                <p className="mt-1 text-[11px] text-[#8a9a8a]">
                  {t("conversation.rental_type_switch_hint", "Tap to switch the rental type")}
                </p>
              )}
            </div>
          )}

          {/* Dates & times */}
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5a6b5a]">
                {t("conversation.trip_dates", "Dates")}
              </p>
              <p className={`mt-0.5 text-sm ${datesEntered ? "text-brand" : "text-[#8a9a8a]"}`}>
                {datesEntered
                  ? `${dateFrom} ${pickup12} – ${dateTo} ${dropoff12}`
                  : t("conversation.add_dates", "Add dates & times")}
              </p>
            </div>
            <CheckoutDatesEditor
              rentalType={rentalType}
              dateFrom={dateFrom}
              dateTo={dateTo}
              pickupTime={pickup12}
              dropoffTime={dropoff12}
              hours={hours}
              availability={availability}
              loadingAvailability={loadingAvailability}
              onApply={(next) => {
                setDateFrom(next.dateFrom);
                setDateTo(next.dateTo);
                setPickupTime(time12ToHhmm(next.pickupTime));
                setDropoffTime(time12ToHhmm(next.dropoffTime));
                setHours(next.hours);
              }}
              trigger={
                <button type="button" className="text-xs font-medium text-brand underline">
                  {datesEntered ? t("common.change", "Change") : t("common.add", "Add")}
                </button>
              }
            />
          </div>

          <div className="h-px bg-border" />

          {/* Location */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5a6b5a]">
                {t("checkout.pickup_dropoff", "Pick-up & drop-off")}
              </p>
              <p className="mt-0.5 truncate text-sm text-brand">{locationSummary}</p>
            </div>
            <CheckoutLocationEditor
              pickupCity={car?.city || ""}
              pickupState={car?.state || ""}
              carLatitude={car?.latitude ?? null}
              carLongitude={car?.longitude ?? null}
              countryCode={car?.country_code ?? null}
              currency={currency}
              deliveryEnabled={deliveryAllowedForType}
              deliveryMaxRadius={carDeliveryRadius}
              deliveryAirports={carDeliveryAirports}
              currentDistance={effectiveIsDelivery ? deliveryDistance : null}
              currentAirportCode={effectiveIsDelivery ? airportCode : null}
              currentAddress={effectiveIsDelivery ? deliveryAddress : null}
              currentIsDelivery={effectiveIsDelivery}
              neutralLabels
              requireDelivery={deliveryOnly}
              requireDeliveryNote={
                rentalType === "chauffeured"
                  ? t(
                      "conversation.chauffeured_delivery_note",
                      "Chauffeured trips are driven to the renter. Pick-up isn't available.",
                    )
                  : undefined
              }
              onApply={(next) => {
                const nextIsDelivery =
                  !!next.deliveryAirportCode || next.deliveryDistance != null;
                if (nextIsDelivery && !deliveryAllowedForType) return;
                setIsDelivery(nextIsDelivery);
                setDeliveryDistance(next.deliveryDistance);
                setAirportCode(next.deliveryAirportCode);
                setDeliveryAddress(next.deliveryAddress);
                setPickupLat(next.deliveryLatitude);
                setPickupLng(next.deliveryLongitude);
              }}
              trigger={
                <button type="button" className="text-xs font-medium text-brand underline">
                  {t("common.change", "Change")}
                </button>
              }
            />
          </div>

          <div className="h-px bg-border" />

          {/* Fees */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5a6b5a]">
              {t("conversation.fees", "Fees")}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-[#5a6b5a]">
                  {t("conversation.fee_rental", "Rental fee")}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#5a6b5a]">{sym}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={rentalFee}
                    onChange={(e) => {
                      setRentalFeeTouched(true);
                      setRentalFee(e.target.value);
                    }}
                    placeholder={quote?.rentalFee != null ? Number(quote.rentalFee).toFixed(2) : "0.00"}
                    disabled={!detailsComplete}
                    data-testid="build-trip-rental-fee"
                    className={`w-full rounded-lg border border-border bg-muted/30 py-2 ${currencySymbolPadding(sym)} pr-3 text-base md:text-sm text-brand placeholder:text-[#5a6b5a]/50 focus:border-brand/30 focus:outline-none focus:ring-1 focus:ring-brand/10 disabled:cursor-not-allowed disabled:opacity-50`}
                  />
                </div>
                {rentalFloor.available && rentalFloor.amount > 0 && (
                  <p
                    className={`mt-1 text-[11px] ${rentalFeeBelowFloor ? "text-red-600" : "text-[#8a9a8a]"}`}
                    data-testid="build-trip-min-rental-hint"
                  >
                    {t("conversation.min_rental_fee", "Minimum")}: {formatPrice(rentalFloor.amount, currency)}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs text-[#5a6b5a]">
                  {t("conversation.fee_delivery", "Delivery fee")}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#5a6b5a]">{sym}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={deliveryFee}
                    onChange={(e) => {
                      setDeliveryFeeTouched(true);
                      setDeliveryFee(e.target.value);
                    }}
                    placeholder={quote?.deliveryFee != null ? Number(quote.deliveryFee).toFixed(2) : "0.00"}
                    disabled={!detailsComplete || !effectiveIsDelivery}
                    data-testid="build-trip-delivery-fee"
                    className={`w-full rounded-lg border border-border bg-muted/30 py-2 ${currencySymbolPadding(sym)} pr-3 text-base md:text-sm text-brand placeholder:text-[#5a6b5a]/50 focus:border-brand/30 focus:outline-none focus:ring-1 focus:ring-brand/10 disabled:opacity-50`}
                  />
                </div>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-[#8a9a8a]">
              {detailsComplete
                ? t(
                    "conversation.fee_auto_hint",
                    "Auto-filled from the calculated price. Edit a field to override the rental or delivery fee.",
                  )
                : t(
                    "conversation.fee_enter_details_first",
                    "Enter the trip details above to calculate the fees.",
                  )}
            </p>
          </div>

          {/* Optional message */}
          <div>
            <label className="mb-1 block text-xs text-[#5a6b5a]">
              {t("conversation.build_trip_message", "Message (optional)")}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-brand placeholder:text-[#5a6b5a]/50 focus:border-brand/30 focus:outline-none focus:ring-1 focus:ring-brand/10"
              placeholder={t("conversation.build_trip_message_ph", "Add a note for the renter…")}
            />
          </div>

          {/* Price breakdown — only shown once the host has entered the trip. */}
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
            {!detailsComplete ? (
              <p className="text-center text-xs text-[#8a9a8a]" data-testid="build-trip-price-hint">
                {locationRequired
                  ? t(
                      "conversation.enter_details_for_price_location",
                      "Enter the dates and delivery location to see pricing",
                    )
                  : t("conversation.enter_details_for_price", "Enter the trip details to see pricing")}
              </p>
            ) : quoteLoading ? (
              <div className="flex justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-[#8a9a8a]" />
              </div>
            ) : quoteError ? (
              <span className="text-xs text-red-600" data-testid="build-trip-quote-error">{quoteError}</span>
            ) : quote ? (
              <div className="space-y-1.5 text-sm" data-testid="build-trip-breakdown">
                <div className="flex items-center justify-between">
                  <span className="text-[#5a6b5a]">{t("conversation.fee_rental", "Rental fee")}</span>
                  <span className="text-brand">{formatPrice(quote.rentalFee, currency)}</span>
                </div>
                {(effectiveIsDelivery || quote.deliveryFee > 0) && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#5a6b5a]">{t("conversation.fee_delivery", "Delivery fee")}</span>
                    <span className="text-brand">{formatPrice(quote.deliveryFee, currency)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-[#5a6b5a]">{t("conversation.fee_service", "Service fee")}</span>
                  <span className="text-brand">{formatPrice(quote.serviceFee, currency)}</span>
                </div>
                {quote.insuranceFee > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#5a6b5a]">{t("conversation.fee_insurance", "Insurance")}</span>
                    <span className="text-brand">{formatPrice(quote.insuranceFee, currency)}</span>
                  </div>
                )}
                <div className="my-1 h-px bg-border" />
                <div className="flex items-center justify-between font-semibold">
                  <span className="text-[#5a6b5a]">{t("conversation.renter_pays", "Renter pays")}</span>
                  <span className="text-brand" data-testid="build-trip-total">
                    {formatPrice(quote.paymentDue, currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#5a6b5a]">{t("conversation.my_earnings", "My earnings")}</span>
                  <span className="font-medium text-brand" data-testid="build-trip-earnings">
                    {formatPrice(quote.ownerEarn, currency)}
                  </span>
                </div>
              </div>
            ) : (
              <span className="text-[#8a9a8a]">—</span>
            )}
          </div>

          {sendError && (
            <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
              {sendError}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-[#5a6b5a] transition hover:bg-muted/50"
            >
              {t("common.cancel", "Cancel")}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sendDisabled}
              data-testid="build-trip-send"
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("conversation.send_quote", "Send quote")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
